/**
 * The portal's property filter (tracker item 45): "All properties" by
 * default, then one chip per property (a select when there are many). It is
 * hidden entirely when the customer has one property, and the choice is
 * remembered per device in localStorage (never fails when storage is blocked).
 */
import React, { useCallback, useState } from 'react';
import { PortalProperty } from '../../api/types';
import {
  ALL_PROPERTIES,
  effectivePropertyFilter,
  loadPropertyFilter,
  portalAddress,
  savePropertyFilter,
  showPropertyFilter,
} from '../../utils/portal';
import { inputClass } from '../../components/formStyles';

/** The filter value (a property id or ALL) with its per-device memory for this login. */
export function usePropertyFilter(userId: string, choices: readonly PortalProperty[]): [string, (value: string) => void] {
  const [stored, setStored] = useState(() => loadPropertyFilter(userId));
  const set = useCallback((value: string) => {
    savePropertyFilter(userId, value);
    setStored(value);
  }, [userId]);
  return [effectivePropertyFilter(stored, choices), set];
}

const MAX_CHIPS = 4;

interface Props {
  choices: readonly PortalProperty[];
  value: string;
  onChange: (value: string) => void;
}

export function PropertyFilter({ choices, value, onChange }: Props) {
  if (!showPropertyFilter(choices)) return null;
  const chip = (active: boolean) =>
    `min-h-[44px] max-w-full rounded-full px-4 text-left text-sm font-semibold break-words ${
      active ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 ring-1 ring-gray-300 hover:bg-gray-50'
    }`;

  if (choices.length > MAX_CHIPS) {
    return (
      <div className="mb-4">
        <label htmlFor="property-filter" className="mb-1 block text-sm font-medium text-gray-700">Property</label>
        <select id="property-filter" className={inputClass + ' min-h-[44px]'} value={value} onChange={(e) => onChange(e.target.value)}>
          <option value={ALL_PROPERTIES}>All properties</option>
          {choices.map((p) => <option key={p.id} value={p.id}>{portalAddress(p)}</option>)}
        </select>
      </div>
    );
  }
  return (
    <div className="mb-4 flex flex-wrap gap-2" role="group" aria-label="Filter by property">
      <button type="button" className={chip(value === ALL_PROPERTIES)} aria-pressed={value === ALL_PROPERTIES} onClick={() => onChange(ALL_PROPERTIES)}>
        All properties
      </button>
      {choices.map((p) => (
        <button key={p.id} type="button" className={chip(value === p.id)} aria-pressed={value === p.id} onClick={() => onChange(p.id)}>
          {p.address}
        </button>
      ))}
    </div>
  );
}
